import type {RawComponent} from './library_component.ts';

export type RawComponentPair = {
  rawHead: RawComponent | undefined,
  rawBase: RawComponent | undefined,
};

/**
 * Pair head and base raw entities (pages or components) for the diff view,
 * accounting for crysknife's per-page rename alignment.  When `baseIdByHeadId`
 * pairs head id A to base id B, both sides land in the same result entry.
 * A naive union-by-id without the alignment would yield two separate entries
 * — added (A) and removed (B) — for what crysknife considers one logical
 * page.  Head order is preserved; base-only entries (deleted pages, or
 * unrenamed pages absent from head) are appended in base order.
 */
export function joinPagesByAlignment(
  rawHead: RawComponent[],
  rawBase: RawComponent[],
  baseIdByHeadId: Map<string, string>,
): RawComponentPair[] {
  const baseById = new Map<string, RawComponent>();
  for (const c of rawBase) baseById.set(c.id, c);

  const consumedBaseIds = new Set<string>();
  const result: RawComponentPair[] = [];

  // Walk head order first, looking up base counterparts via the alignment map.
  // A given base entry can only pair to one head — if alignment and direct-id
  // lookup collide on the same base id, the first head wins.
  for (const head of rawHead) {
    const baseId = baseIdByHeadId.get(head.id) ?? head.id;
    let base: RawComponent | undefined;
    if (!consumedBaseIds.has(baseId)) {
      base = baseById.get(baseId);
      if (base) consumedBaseIds.add(baseId);
    }
    result.push({rawHead: head, rawBase: base});
  }

  // Append base-only entries (deleted pages, unrenamed extras) not already
  // paired above.
  for (const base of rawBase) {
    if (consumedBaseIds.has(base.id)) continue;
    result.push({rawHead: undefined, rawBase: base});
  }

  return result;
}
